import { canv } from "@/canv/global";
import { reverseTransformPoint } from "@/canv/utils";
import { getFirstSelected } from "@/canvasController/helpers";
import type { elementBase } from "@/elementBase/elementBase";
import { cardWidget } from "@/elements/card/card";
import { frameWidget } from "@/elements/frame";
import { mindMapWidget } from "@/elements/mindMap/mindMap";
import { TableWidget } from "@/elements/table/table";
import type { textWidget } from "@/elements/text/text";
import type { TextWidgetV2 } from "@/elements/text/textV2";

export class EditTextWindow {
    show<T extends elementBase>(element: T) {
        canv.widgetMenu.hide();
        this.hide();
        if (element.constructorName === "text" || element.constructorName === "textV2") {
            this.textWidget = element as unknown as textWidget | TextWidgetV2;
        } else if ("textPart" in element) {
            this.textWidget = element.textPart as textWidget | TextWidgetV2;
            this.parentElement = element;
        } else if (element instanceof frameWidget) {
            this.textWidget = element.textName as textWidget | TextWidgetV2;
            this.parentElement = element;
        } else if (element instanceof TableWidget) {
            this.textWidget = element.getOnlyCell().textPart;
            this.parentElement = element;
        } else if (element instanceof cardWidget) {
            this.textWidget = element.textDescription;
            if (element.interactionType === "authorFocus") {
                this.textWidget = element.textAuthor;
            } else if (element.interactionType === "titleFocus") {
                this.textWidget = element.textTitle;
            }
            this.parentElement = element;
        } else if (element instanceof mindMapWidget) {
            this.textWidget = element.getOnlySelectedNode().textPart;
            this.parentElement = element;
        }

        if (!this.textWidget) {
            throw new Error("We can't open edit modal without text element");
        }

        this.textWidget.isFocused = false;
        this.textWidget.isEditTextMobile = true;

        const editTextWindow = document.createElement("div");
        document.body.prepend(editTextWindow);

        editTextWindow.classList.add("editTextWindow");
        editTextWindow.classList.add("dragThrowAble");
        editTextWindow.innerHTML = this.getBar();

        const editTextWindowInput = document.getElementsByClassName(
            "editTextWindowInput",
        )[0] as HTMLInputElement;
        editTextWindowInput.value = this.textWidget.getTextByLines();

        setTimeout(() => {
            editTextWindowInput.focus();
        }, 0);

        this.locate();
        this.init();
    }

    locate() {
        const selectedProps = canv.canvasController.getSelectedProps();

        const startX = selectedProps.startX;
        const startY = selectedProps.startY;
        const width = selectedProps.width;

        const menu = document.getElementsByClassName("editTextWindow")[0] as HTMLDivElement;
        if (menu === undefined) {
            return;
        }

        if (menu.style.opacity === "0") {
            menu.style.opacity = "1";
            menu.style.marginTop = "0px";
        }

        const menuSize = menu.getBoundingClientRect();

        let xTarget = (startX + width / 2) * canv.m[0];
        let yTarget = startY * canv.m[3];

        const tool = canv.toolBox.TryGetToolByName("cursorTool");

        if (tool?.isMode("interaction")) {
            const interactionLocale = getFirstSelected().locateWidgetMenu();

            if (interactionLocale?.status) {
                xTarget = interactionLocale.x * canv.m[3];
                yTarget = interactionLocale.y * canv.m[3];
            }
        }

        const pt = reverseTransformPoint(xTarget, yTarget);

        let wantedX = pt.x - menuSize.width / 2;
        if (wantedX < 10) wantedX = 10;
        if (wantedX + menuSize.width + 10 > canv.canvas.getBoundingClientRect().width)
            wantedX = canv.canvas.getBoundingClientRect().width - menuSize.width - 10;
        menu.style.left = `${wantedX.toString()}px`;

        const gap = 50; //  было 25
        let wantedY = pt.y - menuSize.height - gap;
        if (wantedY < 60) wantedY = 60;
        if (wantedY + menuSize.height + 10 > canv.canvas.getBoundingClientRect().height)
            wantedY = canv.canvas.getBoundingClientRect().height - menuSize.height - 10;
        menu.style.top = `${wantedY.toString()}px`;
    }

    hide() {
        const editTextWindow = document.getElementsByClassName("editTextWindow")[0];

        if (editTextWindow !== undefined) {
            editTextWindow.outerHTML = "";
        }

        this.textWidget = null;
        this.parentElement = null;
    }

    init() {
        const input = document.getElementsByClassName("editTextWindowInput")[0];
        if (input !== undefined) {
            input.addEventListener("focus", this.inputFocus.bind(this), false);
            input.addEventListener("blur", this.inputBlur.bind(this), false);
        }

        const button = document.getElementsByClassName("editTextSaveButton")[0];
        if (button !== undefined) {
            button.addEventListener("click", this.saveText.bind(this));
        }
    }

    inputFocus() {
        canv.appInterface.inputFocused = 1;
    }

    inputBlur() {
        canv.appInterface.inputFocused = 0;
    }

    saveText() {
        const input = document.getElementsByClassName("editTextWindowInput")[0] as HTMLInputElement;

        this.textWidget?.menuMobileEditSet(input.value);
        this.parentElement?.updateDims();

        if (
            this.parentElement instanceof mindMapWidget ||
            this.parentElement instanceof frameWidget
        ) {
            this.parentElement.updateAfterMobileEdit();
        }

        canv.appInterface.inputFocused = 2;

        this.hide();
    }

    getBar() {
        return `
            <textarea type="text" class="editTextWindowInput" autofocus="true" placeholder="Введите текст"></textarea>
            <button class="editTextSaveButton">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.6666 5.41675L7.49992 14.5834L3.33325 10.4167" stroke="#fff" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        `;
    }

    textWidget: textWidget | TextWidgetV2 | null = null;
    parentElement: elementBase | null = null;
}
